import { reactive, computed } from 'vue'
import { Store } from 'vuex'
import { Router } from 'vue-router'
import { State as StoreState } from '@/store/index'
import { DownloadDetailState, DownloadDetailStatusState } from '@/model/components/users/userDownloadDetailState'
import {
  ErrorMsgState,
  useErrorMsg,
  copyData,
  convertLinkingData,
  useErrorMsgString,
} from '@/model/components/common/message/errorMessage'
import * as log from '@/model/util/log/Logger'
import { userDownload, DOWNLOAD_STATUS, PrepareResponseData } from '@/model/api/user/apiUserDownload'
import { useApiError } from '@/model/api/error/apiError'
import { useStoreAction } from '@/model/store/store'
import ERROR_CODE, { ErrorLinkingData, getErrorLinkingData } from '@/model/api/error/errorCodeType'
import { submitById } from '@/model/util/dom/domUtil'
import { getMessage } from '@/i18n/util'

/**
 * 失敗メッセージの取得有無
 * @param props 初期データ
 * @returns 失敗メッセージの取得有無
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isFail = (props: any, disp: boolean) => disp && !!props.failmsg

/**
 * 失敗メッセージ用エラー情報を返す
 * @param props 初期データ
 * @returns ダウンロード詳細の関数
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useErrorFailState = (props: any) => useErrorMsgString(props.failmsg)

/**
 * ダウンロード詳細の関数を返す
 * @param store vuex
 * @returns ダウンロード詳細の関数
 */
const useDownloadDetail = (store: Store<StoreState>) => {
  // ダウンロード詳細情報検索有無の取得
  const isDetail = computed(() => store.getters.downloadSearch)
  // ダウンロード詳細情報の取得
  const detailinfo = computed(() => store.getters.downloadDetail)
  return {
    isDetail,
    detailinfo,
  }
}

/**
 * 初期状態を返す
 * @param props 初期データ
 * @param store vuex
 * @returns 初期状態
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useState = (props: any, store: Store<StoreState>) => {
  // 初期状態
  const state = reactive<DownloadDetailState>({
    loading: false,
    key: store.state.screen.download.detail.key,
    loadingLable: '',
    url: '',
    failDisp: isFail(props, store.getters.downloadDetailFail),
  })
  return state
}

/**
 * ファイルダウンロード中に表示する文字列を取得する
 * @param i18n i18nオブジェクト
 * @returns 文字列
 */
const loadingLabel = (i18n: unknown) => {
  const preparation = getMessage(i18n, 'body.main.download.detail.loading.preparation')
  const completion = getMessage(i18n, 'body.main.download.detail.loading.completion')
  return {
    preparation,
    completion,
  }
}
/**
 * loading処理の設定を実施
 * @param state 状態
 * @param setting 設定するloading値
 * @param i18n i18nオブジェクト
 */
const loading = (state: DownloadDetailState, setting: boolean, i18n: unknown) => {
  state.loading = setting
  if (setting) {
    state.loadingLable = loadingLabel(i18n).preparation
  } else {
    state.loadingLable = ''
  }
}

/**
 * エラー情報の設定
 * @param error 設定されているエラー情報
 * @param errorState エラー情報状態
 * @param store vuex
 */
const setnodata = (error: ErrorMsgState, errorState: ErrorMsgState, store: Store<StoreState>) => {
  // データ無しエラーチェック
  const check = useApiError().isDownloadNodata(error)
  if (check) {
    // データ無しエラーの場合はvuexにエラー情報を設定する
    const data: ErrorMsgState = {
      data: [],
    }
    // データ無しエラーのリソースキーを紐づく複数リソースキーを取得して設定
    convertLinkingData(data, getErrorLinkingData(ERROR_CODE.DOWNLOAD.NODATA.code) as ErrorLinkingData)
    useStoreAction(store).downloadError(data)
  } else {
    // データ無しエラーの場合は詳細情報のエラー情報に設定する
    copyData(error, errorState)
  }
}
/**
 * ダウンロード準備処理を実施
 * @param key ダウンロードキー
 * @param downloadexekey ダウンロード実行キー
 * @param store vuex
 * @param router vue router
 * @param error エラー情報
 * @returns レスポンスデータ(異常の場合はundefined)
 */
const downloadPrepare = async (
  key: string,
  downloadexekey: string,
  store: Store<StoreState>,
  router: Router,
  error: ErrorMsgState
) => {
  // ダウンロード準備api送信を実施
  const { normaldata } = await userDownload(router, store).prepare(key, downloadexekey, error)
  return normaldata
}

/**
 * ダウンロード処理を実施
 * @param state 画面状態
 * @param input ダウンロードurl取得データ
 * @param runStartSleep ダウンロード処理開始sleep(ミリ秒)
 */
const downloadRun = async (state: DownloadDetailState, input: PrepareResponseData, runStartSleep: number) => {
  // 実行前sleepミリ秒
  const sleepVal = input.sleep !== undefined ? input.sleep : 0
  // formにurl及びパラメタを設定
  state.url = input.url
  // formをsumbitしてファイルダウンロードの実施
  // 準備apiのレスポンスにsleepが設定されている場合はsleepミリ秒後に実施する
  log.debug(`filedownload submit before. url:${state.url}, sleepVal:${sleepVal}`)
  window.setTimeout(async () => {
    // formのsubmit
    log.debug('filedownload submit start.')
    submitById('download_form')
  }, sleepVal)
  if (runStartSleep !== 0) {
    // ダウンロード開始処理ミリ秒数が0以外の場合は指定ミリ秒sleepする。テスト時に上記の関数の実行確認をするため。通常は0が設定されているため実施される事はない
    await new Promise((resolve) => {
      setTimeout(resolve, runStartSleep)
    })
  }
  log.debug('filedownload submit end.')
}

/**
 * ダウンロード監視を実施(未使用)
 * @param state 状態
 * @param downloadexekey ダウンロード実行キー
 * @param store vuex
 * @param router vue router
 * @param error エラー情報
 * @param i18n i18nオブジェクト
 */
const downloadStatus = async (
  state: DownloadDetailState,
  downloadexekey: string,
  store: Store<StoreState>,
  router: Router,
  error: ErrorMsgState,
  i18n: unknown
) => {
  // ダウンロード監視api送信を実施
  const { normaldata } = await userDownload(router, store).status(state.key, downloadexekey, error)
  // 監視処理ではERROR_NO_KEY_DATAの場合はデータ削除されているのでシステムエラー扱いにするため、データ無しメッセージは表示しない
  // errorに更新されているものをそのまま表示する
  if (normaldata) {
    // 正常の場合は、loadinglabelを変更する
    switch (normaldata.status) {
      case DOWNLOAD_STATUS.INIT:
      case DOWNLOAD_STATUS.GATEWAY_REQUEST:
      case DOWNLOAD_STATUS.GATEWAY_COMPLETE:
        // サーバー側で処理している場合はファイル準備中を表示
        state.loadingLable = loadingLabel(i18n).preparation
        break
      case DOWNLOAD_STATUS.COMPLETE:
        // サーバー側でダウンロードできる状態になった場合はファイル準備完了を表示
        state.loadingLable = loadingLabel(i18n).completion
        break
      case DOWNLOAD_STATUS.ERROR:
      default:
        // サーバー側でエラー発生または予期せぬデータの場合はラベルに空を設定
        state.loadingLable = ''
        break
    }
  }
}

/**
 * ダウンロード監視の実行スレッドを停止する
 * @param status ダウンロード監視状態
 */
const clearDownloadStatus = (status: DownloadDetailStatusState) => {
  if (status.id !== undefined) {
    log.debug(`clearDownloadStatus. id:${status.id}`)
    window.clearInterval(status.id)
    status.id = undefined
    status.count = 1
  }
}

/**
 * ダウンロード処理を実施する
 * @param state 画面状態
 * @param type ダウンロード種別
 * @param store vuex
 * @param router vueRouter
 * @param error エラーメッセージ状態
 * @param errorFail 失敗エラーメッセージ状態
 * @param i18n i18nオブジェクト
 * @param runStartSleep ダウンロード処理開始sleep(ミリ秒)
 */
const download = async (
  state: DownloadDetailState,
  type: string,
  store: Store<StoreState>,
  router: Router,
  error: ErrorMsgState,
  errorFail: ErrorMsgState,
  i18n: unknown,
  runStartSleep: number
) => {
  // 一旦メッセージを全て初期化
  error.data.splice(0)
  errorFail.data.splice(0)
  const errorTmp: ErrorMsgState = {
    data: [],
  }
  // ダウンロードurlの初期化
  state.url = ''
  // ローディングがONの場合は実行中とみなして何もしない
  if (state.loading) {
    log.debug(`Already running. [download]`)
    return
  }
  // ローディングをONにする
  loading(state, true, i18n)
  // 実行キー生成api送信を実施
  const { normaldata } = await userDownload(router, store).create(state.key, type, errorTmp)
  if (normaldata) {
    // ダウンロード準備処理実施
    const result = await downloadPrepare(state.key, normaldata.downloadexekey, store, router, errorTmp)
    if (result) {
      // 正常の場合はファイルダウンロード処理を実施
      log.debug(`aaa`)
      await downloadRun(state, result, runStartSleep)
    } else {
      // エラーの場合はダウンロード後処理api送信で実行状態をエラーで更新する
      userDownload(router, store).postProcessDownload(state.key, normaldata.downloadexekey, DOWNLOAD_STATUS.ERROR)
      setnodata(errorTmp, error, store)
    }
  } else {
    // エラーの場合はエラー情報に設定
    setnodata(errorTmp, error, store)
  }
  // ローディングをOFFにする
  loading(state, false, i18n)
}

export {
  useDownloadDetail,
  useState,
  loadingLabel,
  download,
  useErrorMsg,
  loading,
  setnodata,
  downloadPrepare,
  downloadRun,
  downloadStatus,
  clearDownloadStatus,
  isFail,
  useErrorFailState,
}
